import {FC} from 'react'
import {Button} from '../../../../components/Button/Button'
import {i18n} from '../../../../translate/i18n'
import {BasicInfoTabProps} from '../types/tabTypes'
import {ReservationRoomStatus, getReservationStatusLabel} from '../types'
import Swal from 'sweetalert2'
import {PUT} from '../../../../services/api'

const BasicInfoTab: FC<BasicInfoTabProps> = ({
  id,
  formData,
  originalReservation,
  rooms,
  customers,
  onInputChange,
  onSave,
  onCancel,
  canEditField,
  getSelectedRoomDetails,
  getSelectedCustomerDetails,
}) => {
  const selectedRoom = getSelectedRoomDetails()
  const selectedCustomer = getSelectedCustomerDetails()

  const generatePassword = (len: number) => {
    return Array.from({length: len}, () => Math.floor(Math.random() * 10).toString()).join('')
  }

  async function changePassword(newPassword: string) {
    try {
      if (!id) {
        throw new Error('Reservation ID is missing')
      }
      const response = await PUT(`/hotel-reservation/${id}/change-password`, {
        password: newPassword,
      })
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Senha alterada',
          text: 'A senha foi alterada com sucesso.',
        })
      } else {
        throw new Error('Failed to change password')
      }
    } catch (error) {
      console.error('Error changing password:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível alterar a senha. Tente novamente mais tarde.',
      })
    }
  }

  function onChangePasswordClick() {
    const newPassword = generatePassword(6)
    Swal.fire({
      title: 'Alterar Senha',
      html: `
      <p>Nova Senha: <strong>${newPassword}</strong></p>
      <p style="margin-top:8px;">
        <span style="color:#d9534f;">
        <i class="fas fa-exclamation-triangle"></i>
        Atenção: ao alterar, a senha antiga não funcionará mais.<br/>
        A nova senha deve ser repassada ao cliente.
        </span>
      </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Alterar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        changePassword(newPassword)
      }
    })
  }

  return (
    <form>
      {/* Status e Avisos */}
      {originalReservation && (
        <div className='alert alert-info mb-5'>
          <strong>{i18n.t('hospitality_room_reservation_tabs.basic_info.label_status')}</strong>{' '}
          {getReservationStatusLabel(originalReservation.status)}
          {!canEditField('roomId') && (
            <div className='mt-2'>
              <small>
                <i className='fas fa-info-circle me-1'></i>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_edit_warning')}
              </small>
            </div>
          )}
        </div>
      )}

      <div className='row'>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.label_room')}
            </label>
            <select
              className='form-select'
              value={formData.roomId}
              onChange={(e) => onInputChange('roomId', e.target.value)}
              disabled={!canEditField('roomId')}
              required
            >
              <option value=''>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.label_room')}
              </option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.roomNumber} - {room.type} (Cap: {room.capacity}) - R${' '}
                  {room.price.toFixed(2)}
                </option>
              ))}
            </select>
            {selectedRoom && (
              <small className='text-muted'>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.selected_room_details', {
                  roomNumber: selectedRoom.roomNumber,
                  type: selectedRoom.type,
                  capacity: selectedRoom.capacity,
                  price: selectedRoom.price.toFixed(2),
                })}
              </small>
            )}
          </div>
        </div>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.label_customer')}
            </label>
            <select
              className='form-select'
              value={formData.customerId}
              onChange={(e) => onInputChange('customerId', e.target.value)}
              disabled={true}
              required
            >
              <option value=''>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.label_customer')}
              </option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.email}
                </option>
              ))}
            </select>
            {selectedCustomer && (
              <small className='text-muted'>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.selected_customer_details', {
                  name: selectedCustomer.name,
                  email: selectedCustomer.email,
                  phone: selectedCustomer.phone || '',
                })}
              </small>
            )}
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.label_checkin')}
            </label>
            <input
              className='form-control'
              type='datetime-local'
              value={formData.checkIn}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputChange('checkIn', e.target.value)
              }
              disabled={!canEditField('checkIn')}
              required
            />
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.label_checkout')}
            </label>
            <input
              className='form-control'
              type='datetime-local'
              value={formData.checkOut}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputChange('checkOut', e.target.value)
              }
              min={formData.checkIn}
              disabled={
                !canEditField('checkOut') ||
                originalReservation?.status === ReservationRoomStatus.CANCELLED ||
                originalReservation?.status === ReservationRoomStatus.COMPLETED
              }
            />
            <small className='text-muted'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.optional_checkout')}
            </small>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.label_status')}
            </label>
            <select
              className='form-select'
              value={formData.status}
              onChange={(e) => onInputChange('status', e.target.value as ReservationRoomStatus)}
              disabled={
                !canEditField('status') ||
                originalReservation?.status === ReservationRoomStatus.CANCELLED ||
                originalReservation?.status === ReservationRoomStatus.COMPLETED
              }
            >
              <option value={ReservationRoomStatus.PENDING}>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_pending')}
              </option>
              <option value={ReservationRoomStatus.CONFIRMED}>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_confirmed')}
              </option>
              <option value={ReservationRoomStatus.IN_PROGRESS}>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_in_progress')}
              </option>
              <option value={ReservationRoomStatus.COMPLETED}>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_completed')}
              </option>
              <option value={ReservationRoomStatus.CANCELLED}>
                {i18n.t('hospitality_room_reservation_tabs.basic_info.status_cancelled')}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* Resumo da Reserva */}
      {selectedRoom && selectedCustomer && formData.checkIn && (
        <div className='card bg-light-primary mb-5'>
          <div className='card-body'>
            <h5 className='card-title'>
              {i18n.t('hospitality_room_reservation_tabs.basic_info.tab_title')}
            </h5>
            <div className='row'>
              <div className='col-md-6'>
                <p>
                  <strong>
                    {i18n.t('hospitality_room_reservation_tabs.basic_info.label_room')}
                  </strong>{' '}
                  {selectedRoom.roomNumber} - {selectedRoom.type}
                </p>
                <p>
                  <strong>
                    {i18n.t('hospitality_room_reservation_tabs.basic_info.label_customer')}
                  </strong>{' '}
                  {selectedCustomer.name}
                </p>
                <p>
                  <strong>
                    {i18n.t('hospitality_room_reservation_tabs.basic_info.summary_capacity')}
                  </strong>{' '}
                  {selectedRoom.capacity}{' '}
                  {i18n.t('hospitality_room_reservation_tabs.basic_info.summary_people')}
                </p>
              </div>
              <div className='col-md-6'>
                <p>
                  <strong>
                    {i18n.t('hospitality_room_reservation_tabs.basic_info.label_checkin')}
                  </strong>{' '}
                  {new Date(formData.checkIn).toLocaleString('pt-BR')}
                </p>
                {formData.checkOut && (
                  <p>
                    <strong>
                      {i18n.t('hospitality_room_reservation_tabs.basic_info.label_checkout')}
                    </strong>{' '}
                    {new Date(formData.checkOut).toLocaleString('pt-BR')}
                  </p>
                )}
                <p>
                  <strong>
                    {i18n.t('hospitality_room_reservation_tabs.basic_info.label_status')}
                  </strong>{' '}
                  {getReservationStatusLabel(formData.status)}
                </p>
              </div>
            </div>
            <div className='row'>
              <div className='col-md-12 d-flex align-items-center justify-content-end'>
                <Button
                  text={i18n.t('hospitality_room_reservation_tabs.basic_info.btn_change_password')}
                  color='danger'
                  size='small'
                  horizontalPadding={8}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={onChangePasswordClick}
                  btnClass='me-3'
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='d-flex justify-content-end'>
        <Button
          text={i18n.t(`buttons.cancel`)}
          color='danger'
          size='small'
          horizontalPadding={8}
          btnAttr={{
            type: 'button',
          }}
          click={onCancel}
          btnClass='me-3'
        />
        <Button
          text={i18n.t(`buttons.save`)}
          color='primary'
          iconColor='#FFF'
          icon='fas fa-save'
          size='small'
          horizontalPadding={8}
          btnAttr={{
            type: 'button',
          }}
          click={onSave}
        />
      </div>
    </form>
  )
}

export default BasicInfoTab
